import { apiClient } from "@uri/typed-api";

export type MediaAttachment =
  | { kind: "inline"; mimeType: string; dataBase64: string; caption?: string }
  | { kind: "file"; mimeType: string; fileUri: string; caption?: string };
import { fullApiDefinition } from "./apiTyping.ts";

type WebhookBase = {
  network: "whatsapp" | "telegram";
  chat: { id: string; username?: string; title?: string };
  author: { id: string; username?: string };
  text: string;
  time: number;
  fromMe?: boolean;
  phoneNumber?: string;
};

export type SupergreenWebhookUpdate =
  | (WebhookBase & {
    event: "message";
    messageId: string;
    quotedMsgId?: string;
    attachments?: MediaAttachment[];
  })
  | (WebhookBase & {
    event: "edit";
    messageId: string;
    quotedMsgId?: string;
    attachments?: MediaAttachment[];
  })
  | (WebhookBase & {
    event: "reaction";
    messageId: string;
    reactionEmoji?: string;
  })
  | (WebhookBase & {
    event: "vote";
    messageId: string;
    vote: { pollName: string; selectedOptionIndices: number[] };
  })
  | (WebhookBase & {
    event: "disconnected";
  })
  | (WebhookBase & {
    event: "connected";
  })
  | (WebhookBase & {
    event: "banned";
  })
  | (WebhookBase & {
    event: "participant_joined";
    participants: string[];
  })
  | (WebhookBase & {
    event: "delete";
    messageId: string;
  });

export const apiBaseUrl = "https://api.supergreen.cc";

const transientStatuses = new Set([500, 502, 503, 504]);

const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));

const retryDelaysMs = [500, 1500, 4000];

const lastRetryDelay = retryDelaysMs[retryDelaysMs.length - 1];

const errorMessageOf = (body: unknown) =>
  typeof body === "object" && body !== null && "error" in body &&
    typeof body.error === "string"
    ? body.error
    : null;

const hasTransientErrorMessage = (body: unknown) => {
  const error = errorMessageOf(body);
  return error !== null && /try again/i.test(error);
};

const parseJsonOrText = async (response: Response) => {
  const text = await response.text();
  if (!text) return null;
  try {
    return JSON.parse(text);
  } catch {
    return text;
  }
};

const logTransientRetry = (url: string, reason: string, nextDelayMs: number) =>
  console.warn(
    `[supergreen-client] transient ${reason} from ${url}, retrying in ${nextDelayMs}ms`,
  );

const nextDelayAfter = (delay: number) =>
  retryDelaysMs[retryDelaysMs.indexOf(delay) + 1] ?? lastRetryDelay;

const fetchWithRetry = async (url: string, body: string) => {
  for (const delay of [0, ...retryDelaysMs]) {
    if (delay) await sleep(delay);
    try {
      const response = await fetch(url, { method: "POST", body });
      if (transientStatuses.has(response.status)) {
        if (delay === lastRetryDelay) return response;
        logTransientRetry(
          url,
          `status ${response.status}`,
          nextDelayAfter(delay),
        );
        continue;
      }
      if (response.status !== 200) return response;
      const parsed = await parseJsonOrText(response);
      if (!hasTransientErrorMessage(parsed)) {
        return new Response(JSON.stringify(parsed), {
          status: 200,
          headers: { "content-type": "application/json" },
        });
      }
      if (delay === lastRetryDelay) {
        return new Response(JSON.stringify(parsed), {
          status: 200,
          headers: { "content-type": "application/json" },
        });
      }
      logTransientRetry(
        url,
        `error body ${JSON.stringify(parsed)}`,
        nextDelayAfter(delay),
      );
    } catch (e) {
      if (delay === lastRetryDelay) throw e;
      logTransientRetry(
        url,
        `network error ${e instanceof Error ? e.message : String(e)}`,
        nextDelayAfter(delay),
      );
    }
  }
  throw new Error("unreachable");
};

const retryingHttpCommunication =
  (serverUrl: string) => async <T, O>(params: T): Promise<O> => {
    const response = await fetchWithRetry(serverUrl, JSON.stringify(params));
    if (response.status === 200) return response.json() as Promise<O>;
    throw new Error(`HTTP ${response.status}: ${await response.text()}`);
  };

export default apiClient(
  retryingHttpCommunication(apiBaseUrl),
  fullApiDefinition,
);

export * from "./webhooks.ts";
